import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_KEYWORDS_DAILY_STATS_FOR_PROJECT } from '../../../../query/keyword/stats/keys';
import { getKeywordsDailyStatsForProject } from '../../../../../services/monitor/keywords/stats/KeywordsStatsService';
import { ChatType } from '../../../../../types/monitor/ChatType';
import { PeriodType } from '../../../../../types/prompt/PeriodType';
import { ProjectStatsResponse } from '../../../../../services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';

const EMPTY_RESPONSE: ProjectStatsResponse = {
  monitorPromptsKeywordsStatsResponse: [],
};

export const useGetKeywordsDailyStatsForProject = (
  projectId: number | null,
  chatType: ChatType | null,
  period: PeriodType | null,
  from: Date | null,
  to: Date | null
) => {
  const queryClient = useQueryClient();
  const queryEnabled = !!projectId && !!chatType && !!period && !!from && !!to;

  const fromTimestamp = from?.getTime() ?? null;
  const toTimestamp = to?.getTime() ?? null;

  const {
    data: keywordsDailyStatsForProject,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ProjectStatsResponse>({
    queryKey: [
      GET_KEYWORDS_DAILY_STATS_FOR_PROJECT,
      projectId,
      chatType,
      period,
      fromTimestamp,
      toTimestamp,
    ],
    queryFn: () => {
      return getKeywordsDailyStatsForProject(projectId!, chatType!, period!, from, to);
    },
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    retry: 3,
    retryDelay: 1000,
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    return queryClient.invalidateQueries({
      queryKey: [
        GET_KEYWORDS_DAILY_STATS_FOR_PROJECT,
        projectId,
        chatType,
        period,
        fromTimestamp,
        toTimestamp,
      ],
      refetchType: 'active',
    });
  };

  return {
    keywordsDailyStatsForProject: keywordsDailyStatsForProject ?? EMPTY_RESPONSE,
    isLoading: queryEnabled && isLoading,
    isError: queryEnabled && isError,
    error: queryEnabled ? error : null,
    refetch,
    invalidate,
  };
};
